import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { usePublicSession, useShortUrl } from '@app/api';
import { setLocale } from '@app/i18n';

import {
  Button,
  FAQItem,
  InformationBox,
  PublicBackground,
  PublicBackgroundBlock,
} from '@app/core';

import {
  AnalyticsCategoryProvider,
  Categories,
  useTrackingParams,
} from '@app/analytics';
import { Theme } from '@app/theme';
import { createUseStyles } from 'react-jss';
import { Trans, useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  block: {
    maxWidth: theme.maxContentWidth.screen,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  title: {
    ...theme.fonts.h3,
    marginBottom: theme.marginBase * 2,
  },
  content: {
    ...theme.fonts.body,
    color: theme.colors.inkPrimary,
    whiteSpace: 'pre-wrap',

    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  faqContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  bottom: {
    marginTop: theme.marginBase * 4,
  },
  buttonLines: {
    marginTop: theme.marginBase * 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.marginBase * 2,
    flexWrap: 'wrap',
    [theme.media.mobile]: {
      flexDirection: 'column',
    },
  },
}));

const allowedPanel: Record<string, string> = {
  maximiles: 'Maximiles',
  m3panel: 'M3panel',
  meinungsplatz: 'Meinungsplatz',
  ivox: 'IVOX',
  clubnuoveidee: 'Club Nuove Idee',
  mingle: 'Mingle',
};

const components = {
  b: <b />,
};

export const TandemzPresentation = ({
  unmoderated = false,
}: {
  unmoderated?: boolean;
}) => {
  const { id } = useParams<{ id: string }>();
  const { data: session } = usePublicSession(id);
  const { t } = useTranslation();
  const classes = useStyles();
  const shortUrl = useShortUrl();

  const tracking = useTrackingParams();
  const locale = session?.locale;
  useEffect(() => {
    setLocale(locale);
  }, [locale]);

  const panel = allowedPanel[String(tracking.panel)]
    ? String(tracking.panel)
    : 'maximiles';

  const notInterestedUrl = useMemo(() => {
    const url = new URL(
      `${shortUrl}/session-links/${id}/not-interested-redirect`,
    );
    Object.entries(tracking || {}).forEach(([key, value]) =>
      url.searchParams.set(key, String(value)),
    );
    return url.toString();
  }, [id, tracking, shortUrl]);

  const faqTrans = t('presentation.faq', { returnObjects: true });
  const faqs: any[] = Array.isArray(faqTrans) ? faqTrans : [];

  const params = {
    panel: allowedPanel[panel],
    context: panel,
  };

  return (
    <AnalyticsCategoryProvider category={Categories.SURVEY}>
      <PublicBackground noLogin customContent>
        <PublicBackgroundBlock className={classes.block}>
          <h1 className={classes.title}>{t('presentation.title', params)}</h1>

          <section className={classes.content}>
            <p>
              <Trans
                i18nKey="presentation.content"
                values={params}
                components={components}
              />
            </p>
            <p>
              <Trans
                i18nKey={
                  unmoderated
                    ? 'presentation.contentListUnmoderated'
                    : 'presentation.contentList'
                }
                values={params}
                components={components}
              />
            </p>
            <p>
              <Trans i18nKey="presentation.contentDuration" />
            </p>
            <p>
              <Trans
                i18nKey="presentation.contentAfter"
                values={params}
                components={components}
              />
            </p>
          </section>
          {faqs.map((faq) => (
            <FAQItem question={faq.title}>
              <div className={classes.faqContent}>
                {faq.content.map((text: string) => (
                  <p>{text}</p>
                ))}
              </div>
            </FAQItem>
          ))}

          <section className={classes.bottom}>
            <InformationBox text={t('presentation.finalInfo')} />
            <div className={classes.buttonLines}>
              <Button
                text={t('presentation.next')}
                withNextArrow
                to={`/s/${id}`}
                fullMobileWidth
              />
              <Button
                text={t('presentation.notInterested')}
                variant="transparent"
                color="red"
                fullMobileWidth
                href={notInterestedUrl}
              />
            </div>
          </section>
        </PublicBackgroundBlock>
      </PublicBackground>
    </AnalyticsCategoryProvider>
  );
};
